import React, { useState, useContext, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Typography, 
  Button, 
  TableSortLabel,
  Autocomplete,
  TextField,
  Box,
  CircularProgress 
} from '@mui/material';
import ErrorDialog from '../ErrorDialog';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContextProvider';
import { Link } from 'react-router-dom';
import { rpc } from '../utils/RpcUtils';
import * as Constants from '../Constants';
import '../App.css';

const EventList = () => {
  const [events, setEvents] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoading(true);
      try {
        const params = {
          _site: user._site._id,
        };
        await rpc({
          method: "GetEvents",
          params,
          setErrorMessage,
          setOpenErrorDialog,
          onSuccess: setEvents
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchEvents();
  }, [user]);
  
  // --- Helper Methods ---

  const onUpload = () => {
    navigate('/uploadVideo');
  };

  const createBingo = () => {
    navigate(`/createEvent/${Constants.EVENT_BINGO}`);
  };

  const createJitsi = () => {
    navigate(`/createEvent/${Constants.EVENT_JITSI}`);
  };

  const createText = () => {
    navigate(`/createEvent/${Constants.EVENT_TEXT_EVENT}`);
  };

  const handleClose = () => {
    setOpenErrorDialog(false);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleAutocompleteSelect = (event, selectedEvent) => {
    if (selectedEvent && selectedEvent._id) {
      navigate(`/event/${selectedEvent._id}`);
    }
  };

  // --- Sorting Logic ---

  const sortedEvents = events.slice().sort((a, b) => {
    if (orderBy === 'name') {
      const nameA = a.name || "";
      const nameB = b.name || "";
      return (order === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA));
    }
    return 0;
  });

  const getButtons = () => {
    const bingo = events.find((event) => event.type === Constants.EVENT_BINGO);
    const jitsi = events.find((event) => event.type === Constants.EVENT_JITSI);

    return (
      <div className="HorizontalContainer" style={{ marginTop: '20px' }}>
        <Button className="Button" variant="contained" color="primary" onClick={onUpload}>
          Upload Video
        </Button>
        {!bingo && (
          <Button className="Button" variant="outlined" color="primary" onClick={createBingo}>
            Bingo
          </Button>
        )}
        {!jitsi && (
          <Button className="Button" variant="outlined" color="primary" onClick={createJitsi}>
            Video Conference
          </Button>
        )}
        <Button className="Button" variant="outlined" color="primary" onClick={createText}>
          Reminder
        </Button>
      </div>
    );
  };
    
  const getPlayImmediately = (event) => {
    const playAttr = event.attributes.find((attr) => {
      return (attr.name === Constants.ATTR_PLAY_IMMEDIATELY);
    });
    return playAttr ? playAttr.value : 'false';
  };

  return (
    <div className="EventList" style={{ padding: '20px' }}>
      {/* Quick Search Header: 
          Only displays if the list size is greater than 10 
      */}
      {events.length > 10 && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 3, 
          gap: 2,
          backgroundColor: '#f5f5f5',
          padding: '15px',
          borderRadius: '8px'
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Find Event:
          </Typography>
          <Autocomplete
            disablePortal
            id="event-search-nav"
            options={events}
            // Allow search by Internal Name or Display Name
            getOptionLabel={(option) => `${option.name} (${option.displayName || 'No Display Name'})`}
            sx={{ width: 400, backgroundColor: 'white' }}
            onChange={handleAutocompleteSelect}
            renderInput={(params) => (
              <TextField {...params} label="Search events..." variant="outlined" size="small" />
            )}
          />
        </Box>
      )}

      {/* Main Table or Loading Spinner */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer className="TableContainer" component={Paper}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      <b>Name</b>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell><b>Display Name</b></TableCell>
                  <TableCell><b>Description</b></TableCell>
                  <TableCell><b>Type</b></TableCell>
                  <TableCell><b>Immediate</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedEvents.map((event) => (
                  <TableRow key={event._id} hover>
                    <TableCell>
                      <Link to={`/event/${event._id}`}>{event.name}</Link>
                    </TableCell>
                    <TableCell>{event.displayName}</TableCell>
                    <TableCell>{event.description}</TableCell>
                    <TableCell>{Constants.EVENT_NAMES[event.type]}</TableCell>
                    <TableCell>{getPlayImmediately(event)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {getButtons()}
        </>
      )}

      <ErrorDialog 
        open={openErrorDialog} 
        onErrorDialogClose={handleClose} 
        errorMessage={errorMessage} 
      />
    </div>
  );
};

export default EventList;